import { RESOURCE_LIMITS } from '../config.js';
import { z } from 'zod';
import {
  calculateIntegralMetrics,
  calculateSaturationMetrics,
  calculateStepMetrics,
  compareTimeSteps,
  evaluateLinearStability,
  makeCandidateId,
  rankFeasibleCandidates,
  simulateScenario,
  validateScenario,
  type ControlScenario,
  type IntegralMetrics,
  type RefinementCriteria,
  type RefinementResult,
  type SaturationMetrics,
  type SimulationResult,
  type StabilityResult,
  type StepMetricSettings,
  type StepMetrics,
  type TuningCandidate,
} from './engine.js';

export const APP_VERSION = 'aerocontrol-app/1.1.0';
export const ENGINE_VERSION = 'aerocontrol-engine/1.0';
export const SCHEMA_VERSION = 1;

export type AngleUnit = 'deg' | 'rad';
export type EvidenceState = 'met' | 'not_met' | 'not_assessed';
export type RunStatus = 'completed' | 'invalid' | 'numerical_failure' | 'cancelled';

export type MetricId =
  | 'rise_time_s'
  | 'overshoot_percent'
  | 'settling_time_s'
  | 'final_abs_error_rad'
  | 'iae_rad_s'
  | 'ise_rad2_s'
  | 'itae_rad_s2'
  | 'control_absolute_nms'
  | 'control_squared_nm2s'
  | 'saturation_fraction'
  | 'saturation_duration_s'
  | 'maximum_raw_torque_nm';

export type ConstraintComparator = '<=' | '<' | '>=' | '>' | 'abs<=';

export interface ConstraintDefinition {
  readonly id: string;
  readonly metric: MetricId;
  readonly comparator: ConstraintComparator;
  readonly threshold: number;
  readonly required: boolean;
  readonly unit: string;
  readonly rationale: string;
}

export interface ConstraintResult {
  readonly id: string;
  readonly metric: MetricId;
  readonly comparator: ConstraintComparator;
  readonly threshold: number;
  readonly required: boolean;
  readonly unit: string;
  readonly rationale: string;
  readonly value: number | null;
  readonly state: EvidenceState;
  readonly reason: string;
}

export interface TuningGrid {
  readonly kp: readonly number[];
  readonly ki: readonly number[];
  readonly kd: readonly number[];
  readonly derivativeFilterTimeConstant: readonly number[];
  readonly trackingTimeConstant: readonly number[];
}

export interface TuningSettings {
  readonly grid: TuningGrid;
  readonly constraints: readonly ConstraintDefinition[];
  readonly primaryObjective: MetricId;
  readonly requireLinearStability: boolean;
  readonly skipAnalyticallyUnstable: boolean;
  readonly maximumCandidates: number;
}

export interface ScenarioDocument {
  readonly schemaVersion: 1;
  readonly id: string;
  readonly name: string;
  readonly intendedQuestion: string;
  readonly parameterProvenance: string;
  readonly assumptions: string;
  readonly displayAngleUnit: AngleUnit;
  readonly notionalDemo: boolean;
  readonly demoId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly scenario: ControlScenario;
  readonly stepMetricSettings: StepMetricSettings;
  readonly refinementCriteria: RefinementCriteria;
  readonly constraints: readonly ConstraintDefinition[];
  readonly tuning: TuningSettings;
}

export interface MetricValue {
  readonly id: MetricId;
  readonly label: string;
  readonly value: number | null;
  readonly unit: string;
  readonly reason: string | null;
}

export interface CalculationTraceItem {
  readonly id: string;
  readonly title: string;
  readonly formula: string;
  readonly substitution: string;
  readonly result: string;
  readonly state: EvidenceState;
  readonly note: string;
}

export interface RunAnalysis {
  readonly simulation: SimulationResult;
  readonly stability: StabilityResult;
  readonly stepMetrics: StepMetrics | null;
  readonly stepMetricsReason: string | null;
  readonly integralMetrics: IntegralMetrics;
  readonly saturationMetrics: SaturationMetrics;
  readonly metrics: readonly MetricValue[];
  readonly constraints: readonly ConstraintResult[];
  readonly constraintsState: EvidenceState;
  readonly warnings: readonly string[];
  readonly traces: readonly CalculationTraceItem[];
  readonly runtimeMs: number;
}

export interface SavedRun {
  readonly id: string;
  readonly scenarioId: string;
  readonly scenarioName: string;
  readonly createdAt: string;
  readonly scenarioHash: string;
  readonly engineVersion: string;
  readonly appVersion: string;
  readonly status: 'completed';
  readonly scenarioSnapshot: ScenarioDocument;
  readonly analysis: RunAnalysis;
  readonly refinement: RefinementResult | null;
}

export interface TuningEvaluation {
  readonly candidate: TuningCandidate;
  readonly stability: StabilityResult;
  readonly constraintResults: readonly ConstraintResult[];
  readonly infeasibilityReasons: readonly string[];
}

export interface TuningResult {
  readonly state: 'completed' | 'cancelled';
  readonly candidateCount: number;
  readonly feasibleCount: number;
  readonly selectedCandidate: TuningCandidate | null;
  readonly evaluations: readonly TuningEvaluation[];
  readonly primaryObjective: MetricId;
  readonly elapsedMs: number;
  readonly selectionTrace: readonly string[];
}

export interface ValidationIssue {
  readonly path: string;
  readonly message: string;
}

export type SafeParseResult<T> =
  | { readonly success: true; readonly data: T }
  | { readonly success: false; readonly issues: readonly ValidationIssue[] };

export function createId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `${prefix}-${uuid}`;
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function angleToDisplay(radians: number, unit: AngleUnit): number {
  return unit === 'deg' ? radians * 180 / Math.PI : radians;
}

export function angleFromDisplay(value: number, unit: AngleUnit): number {
  return unit === 'deg' ? value * Math.PI / 180 : value;
}

export function rateToDisplay(radiansPerSecond: number, unit: AngleUnit): number {
  return angleToDisplay(radiansPerSecond, unit);
}

export function rateFromDisplay(value: number, unit: AngleUnit): number {
  return angleFromDisplay(value, unit);
}

export function angleUnitLabel(unit: AngleUnit): string {
  return unit === 'deg' ? 'deg' : 'rad';
}

export function rateUnitLabel(unit: AngleUnit): string {
  return unit === 'deg' ? 'deg/s' : 'rad/s';
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function pushIssue(issues: ValidationIssue[], path: string, message: string): void {
  issues.push({ path, message });
}

function validateConstraint(constraint: ConstraintDefinition, index: number, issues: ValidationIssue[]): void {
  const path = `constraints[${index}]`;
  if (typeof constraint.id !== 'string' || !constraint.id.trim()) pushIssue(issues, `${path}.id`, 'Constraint ID is required.');
  const metrics: readonly MetricId[] = [
    'rise_time_s', 'overshoot_percent', 'settling_time_s', 'final_abs_error_rad',
    'iae_rad_s', 'ise_rad2_s', 'itae_rad_s2', 'control_absolute_nms',
    'control_squared_nm2s', 'saturation_fraction', 'saturation_duration_s', 'maximum_raw_torque_nm',
  ];
  if (!metrics.includes(constraint.metric)) pushIssue(issues, `${path}.metric`, 'Metric is not supported.');
  if (!finite(constraint.threshold)) pushIssue(issues, `${path}.threshold`, 'Threshold must be finite.');
  if (!['<=', '<', '>=', '>', 'abs<='].includes(constraint.comparator)) {
    pushIssue(issues, `${path}.comparator`, 'Comparator is not supported.');
  }
  if (typeof constraint.required !== 'boolean') pushIssue(issues, `${path}.required`, 'Required flag must be Boolean.');
  if (typeof constraint.unit !== 'string') pushIssue(issues, `${path}.unit`, 'Unit must be text.');
  if (typeof constraint.rationale !== 'string') pushIssue(issues, `${path}.rationale`, 'Rationale must be text.');
}

function validateGridValues(name: string, values: readonly number[], issues: ValidationIssue[]): void {
  if (!Array.isArray(values) || values.length === 0) {
    pushIssue(issues, `tuning.grid.${name}`, 'At least one value is required.');
    return;
  }
  const seen = new Set<string>();
  values.forEach((value, index) => {
    if (!finite(value) || value < 0) {
      pushIssue(issues, `tuning.grid.${name}[${index}]`, 'Value must be finite and non-negative.');
    }
    const key = Object.is(value, -0) ? '0' : String(value);
    if (seen.has(key)) pushIssue(issues, `tuning.grid.${name}[${index}]`, 'Duplicate grid values are not allowed.');
    seen.add(key);
  });
}

export function tuningCandidateCount(settings: TuningSettings, antiWindupEnabled: boolean): number {
  const ttCount = antiWindupEnabled ? settings.grid.trackingTimeConstant.length : 1;
  return settings.grid.kp.length * settings.grid.ki.length * settings.grid.kd.length * settings.grid.derivativeFilterTimeConstant.length * ttCount;
}

const metricIdSchema = z.enum([
  'rise_time_s', 'overshoot_percent', 'settling_time_s', 'final_abs_error_rad',
  'iae_rad_s', 'ise_rad2_s', 'itae_rad_s2', 'control_absolute_nms',
  'control_squared_nm2s', 'saturation_fraction', 'saturation_duration_s', 'maximum_raw_torque_nm',
]);

const constraintSchema = z.object({
  id: z.string().trim().min(1),
  metric: metricIdSchema,
  comparator: z.enum(['<=', '<', '>=', '>', 'abs<=']),
  threshold: z.number().finite(),
  required: z.boolean(),
  unit: z.string(),
  rationale: z.string(),
});

const nonNegativeGridSchema = z.array(z.number().finite().nonnegative()).min(1);
const refinementCriterionSchema = z.object({ selected: z.boolean(), tolerance: z.number().finite().nonnegative() });

const scenarioDocumentSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().trim().min(1),
  name: z.string().trim().min(1),
  intendedQuestion: z.string(),
  parameterProvenance: z.string(),
  assumptions: z.string(),
  displayAngleUnit: z.enum(['deg', 'rad']),
  notionalDemo: z.boolean(),
  demoId: z.string().nullable(),
  createdAt: z.string().refine((value) => Number.isFinite(Date.parse(value)), 'Timestamp must be a valid ISO-style date string.'),
  updatedAt: z.string().refine((value) => Number.isFinite(Date.parse(value)), 'Timestamp must be a valid ISO-style date string.'),
  scenario: z.object({}).catchall(z.unknown()),
  stepMetricSettings: z.object({
    riseLowerFraction: z.number().finite(),
    riseUpperFraction: z.number().finite(),
    settlingHoldDuration: z.number().finite(),
    settlingBand: z.object({
      kind: z.enum(['absolute', 'fraction_of_step']),
      value: z.number().finite(),
    }),
  }),
  refinementCriteria: z.object({
    angle: refinementCriterionSchema,
    angularRate: refinementCriterionSchema,
    appliedTorque: refinementCriterionSchema,
  }),
  constraints: z.array(constraintSchema),
  tuning: z.object({
    grid: z.object({
      kp: nonNegativeGridSchema,
      ki: nonNegativeGridSchema,
      kd: nonNegativeGridSchema,
      derivativeFilterTimeConstant: nonNegativeGridSchema,
      trackingTimeConstant: nonNegativeGridSchema,
    }),
    constraints: z.array(constraintSchema),
    primaryObjective: metricIdSchema,
    requireLinearStability: z.boolean(),
    skipAnalyticallyUnstable: z.boolean(),
    maximumCandidates: z.number().int().positive().safe(),
  }),
});

const savedRunSchema = z.object({
  id: z.string().trim().min(1),
  scenarioId: z.string().trim().min(1),
  scenarioName: z.string().trim().min(1),
  createdAt: z.string().refine((value) => Number.isFinite(Date.parse(value)), 'Timestamp must be a valid ISO-style date string.'),
  scenarioHash: z.string().regex(/^[0-9a-f]{64}$/i, 'Scenario hash must be a 64-character SHA-256 hexadecimal value.'),
  engineVersion: z.string().trim().min(1),
  appVersion: z.string().trim().min(1),
  status: z.literal('completed'),
  scenarioSnapshot: z.unknown(),
  analysis: z.object({
    simulation: z.object({
      samples: z.array(z.unknown()).min(1),
    }).catchall(z.unknown()),
  }).catchall(z.unknown()),
  refinement: z.unknown(),
});

function zodIssuePath(path: readonly PropertyKey[]): string {
  return path.reduce<string>((result, part) => typeof part === 'number'
    ? `${result}[${part}]`
    : result ? `${result}.${String(part)}` : String(part), '');
}

function zodIssues(error: z.ZodError): ValidationIssue[] {
  return error.issues.map((issue) => {
    const path = zodIssuePath(issue.path);
    const compatibilityMessages: Readonly<Record<string, string>> = {
      id: 'Scenario ID is required.',
      name: 'Scenario name is required.',
    };
    return { path, message: compatibilityMessages[path] ?? issue.message };
  });
}

export function safeParseScenarioDocument(input: unknown): SafeParseResult<ScenarioDocument> {
  const structural = scenarioDocumentSchema.safeParse(input);
  if (!structural.success) return { success: false, issues: zodIssues(structural.error) };

  const issues: ValidationIssue[] = [];
  const candidate = structural.data as unknown as ScenarioDocument;
  if (candidate.schemaVersion !== 1) pushIssue(issues, 'schemaVersion', 'Only schema version 1 is supported.');
  if (typeof candidate.id !== 'string' || !candidate.id.trim()) pushIssue(issues, 'id', 'Scenario ID is required.');
  if (typeof candidate.name !== 'string' || !candidate.name.trim()) pushIssue(issues, 'name', 'Scenario name is required.');
  if (typeof candidate.intendedQuestion !== 'string') pushIssue(issues, 'intendedQuestion', 'Engineering question must be text.');
  if (typeof candidate.parameterProvenance !== 'string') pushIssue(issues, 'parameterProvenance', 'Parameter provenance must be text.');
  if (typeof candidate.assumptions !== 'string') pushIssue(issues, 'assumptions', 'Assumptions must be text.');
  if (candidate.displayAngleUnit !== 'deg' && candidate.displayAngleUnit !== 'rad') {
    pushIssue(issues, 'displayAngleUnit', 'Angle unit must be deg or rad.');
  }
  if (typeof candidate.notionalDemo !== 'boolean') pushIssue(issues, 'notionalDemo', 'Notional-demo flag must be Boolean.');
  if (candidate.demoId !== null && typeof candidate.demoId !== 'string') pushIssue(issues, 'demoId', 'Demo ID must be text or null.');
  if (typeof candidate.createdAt !== 'string' || !Number.isFinite(Date.parse(candidate.createdAt))) pushIssue(issues, 'createdAt', 'Created timestamp must be a valid ISO-style date string.');
  if (typeof candidate.updatedAt !== 'string' || !Number.isFinite(Date.parse(candidate.updatedAt))) pushIssue(issues, 'updatedAt', 'Updated timestamp must be a valid ISO-style date string.');

  if (typeof candidate.scenario !== 'object' || candidate.scenario === null) {
    pushIssue(issues, 'scenario', 'Control scenario is required.');
  } else {
    try {
      validateScenario(candidate.scenario);
    } catch (error) {
      pushIssue(issues, 'scenario', error instanceof Error ? error.message : String(error));
    }
  }

  const metrics = candidate.stepMetricSettings;
  if (!metrics || !finite(metrics.riseLowerFraction) || !finite(metrics.riseUpperFraction)) {
    pushIssue(issues, 'stepMetricSettings', 'Step metric settings are incomplete.');
  } else {
    if (!(metrics.riseLowerFraction >= 0 && metrics.riseLowerFraction < metrics.riseUpperFraction && metrics.riseUpperFraction <= 1)) {
      pushIssue(issues, 'stepMetricSettings.rise', 'Rise thresholds must satisfy 0 <= lower < upper <= 1.');
    }
    if (!finite(metrics.settlingHoldDuration) || metrics.settlingHoldDuration < 0) {
      pushIssue(issues, 'stepMetricSettings.settlingHoldDuration', 'Settling hold duration must be non-negative.');
    }
    if (!metrics.settlingBand || !finite(metrics.settlingBand.value) || metrics.settlingBand.value <= 0) {
      pushIssue(issues, 'stepMetricSettings.settlingBand', 'Settling band must be positive.');
    } else if (metrics.settlingBand.kind !== 'absolute' && metrics.settlingBand.kind !== 'fraction_of_step') {
      pushIssue(issues, 'stepMetricSettings.settlingBand.kind', 'Settling-band kind is not supported.');
    }
  }

  const refinement = candidate.refinementCriteria;
  if (!refinement || typeof refinement !== 'object') {
    pushIssue(issues, 'refinementCriteria', 'Refinement criteria are required.');
  } else {
    for (const key of ['angle', 'angularRate', 'appliedTorque'] as const) {
      const criterion = refinement[key];
      if (!criterion || typeof criterion.selected !== 'boolean' || !finite(criterion.tolerance) || criterion.tolerance < 0) {
        pushIssue(issues, `refinementCriteria.${key}`, 'Criterion must contain a Boolean selection and a finite non-negative tolerance.');
      }
    }
  }

  if (!Array.isArray(candidate.constraints)) {
    pushIssue(issues, 'constraints', 'Constraint list is required.');
  } else {
    candidate.constraints.forEach((constraint, index) => {
      if (typeof constraint !== 'object' || constraint === null) pushIssue(issues, `constraints[${index}]`, 'Constraint must be an object.');
      else validateConstraint(constraint, index, issues);
    });
  }

  const tuning = candidate.tuning;
  if (!tuning || typeof tuning !== 'object') {
    pushIssue(issues, 'tuning', 'Tuning settings are required.');
  } else {
    if (!tuning.grid || typeof tuning.grid !== 'object') {
      pushIssue(issues, 'tuning.grid', 'Tuning grid is required.');
    } else {
      validateGridValues('kp', tuning.grid.kp, issues);
      validateGridValues('ki', tuning.grid.ki, issues);
      validateGridValues('kd', tuning.grid.kd, issues);
      validateGridValues('derivativeFilterTimeConstant', tuning.grid.derivativeFilterTimeConstant, issues);
      validateGridValues('trackingTimeConstant', tuning.grid.trackingTimeConstant, issues);
    }
    if (!Array.isArray(tuning.constraints)) {
      pushIssue(issues, 'tuning.constraints', 'Tuning constraint list is required.');
    } else {
      tuning.constraints.forEach((constraint, index) => validateConstraint(constraint, index, issues));
    }
    if (!Number.isSafeInteger(tuning.maximumCandidates) || tuning.maximumCandidates <= 0) {
      pushIssue(issues, 'tuning.maximumCandidates', 'Maximum candidates must be a positive safe integer.');
    } else if (tuning.maximumCandidates > RESOURCE_LIMITS.tuningCandidates) {
      pushIssue(issues, 'tuning.maximumCandidates', `Maximum candidates cannot exceed the disclosed software limit ${RESOURCE_LIMITS.tuningCandidates}.`);
    }
    const metricIds: readonly MetricId[] = [
      'rise_time_s', 'overshoot_percent', 'settling_time_s', 'final_abs_error_rad',
      'iae_rad_s', 'ise_rad2_s', 'itae_rad_s2', 'control_absolute_nms',
      'control_squared_nm2s', 'saturation_fraction', 'saturation_duration_s', 'maximum_raw_torque_nm',
    ];
    if (!metricIds.includes(tuning.primaryObjective)) pushIssue(issues, 'tuning.primaryObjective', 'Primary objective is not supported.');
    if (typeof tuning.requireLinearStability !== 'boolean') pushIssue(issues, 'tuning.requireLinearStability', 'Linear-stability requirement must be Boolean.');
    if (typeof tuning.skipAnalyticallyUnstable !== 'boolean') pushIssue(issues, 'tuning.skipAnalyticallyUnstable', 'Analytical-filter selection must be Boolean.');
    const validGridShape = tuning.grid
      && Array.isArray(tuning.grid.kp)
      && Array.isArray(tuning.grid.ki)
      && Array.isArray(tuning.grid.kd)
      && Array.isArray(tuning.grid.derivativeFilterTimeConstant)
      && Array.isArray(tuning.grid.trackingTimeConstant);
    if (validGridShape && candidate.scenario?.controller) {
      const count = tuningCandidateCount(tuning, candidate.scenario.controller.antiWindup.enabled);
      if (!Number.isSafeInteger(count) || count <= 0) pushIssue(issues, 'tuning.grid', 'Candidate count must be a positive safe integer.');
      else if (count > tuning.maximumCandidates) pushIssue(issues, 'tuning.grid', `Candidate count ${count} exceeds the visible cap ${tuning.maximumCandidates}.`);
    }
  }

  return issues.length === 0
    ? { success: true, data: candidate as ScenarioDocument }
    : { success: false, issues };
}

function metricLabel(id: MetricId): string {
  const labels: Record<MetricId, string> = {
    rise_time_s: 'Rise time',
    overshoot_percent: 'Peak overshoot',
    settling_time_s: 'Settling time',
    final_abs_error_rad: 'Absolute final error',
    iae_rad_s: 'Integral absolute error',
    ise_rad2_s: 'Integral squared error',
    itae_rad_s2: 'Time-weighted absolute error',
    control_absolute_nms: 'Absolute control effort',
    control_squared_nm2s: 'Squared control effort',
    saturation_fraction: 'Saturation fraction',
    saturation_duration_s: 'Saturation duration',
    maximum_raw_torque_nm: 'Maximum raw torque',
  };
  return labels[id];
}

function metricUnit(id: MetricId): string {
  const units: Record<MetricId, string> = {
    rise_time_s: 's',
    overshoot_percent: '%',
    settling_time_s: 's',
    final_abs_error_rad: 'rad',
    iae_rad_s: 'rad·s',
    ise_rad2_s: 'rad²·s',
    itae_rad_s2: 'rad·s²',
    control_absolute_nms: 'N·m·s',
    control_squared_nm2s: '(N·m)²·s',
    saturation_fraction: '1',
    saturation_duration_s: 's',
    maximum_raw_torque_nm: 'N·m',
  };
  return units[id];
}

export function buildMetricValues(
  step: StepMetrics | null,
  stepReason: string | null,
  integral: IntegralMetrics,
  saturation: SaturationMetrics,
): MetricValue[] {
  const values: Record<MetricId, { value: number | null; reason: string | null }> = {
    rise_time_s: { value: step?.riseTime ?? null, reason: step?.riseTimeReason ?? stepReason },
    overshoot_percent: { value: step?.overshootPercent ?? null, reason: step?.overshootReason ?? stepReason },
    settling_time_s: { value: step?.settlingTime ?? null, reason: step?.settlingReason ?? stepReason },
    final_abs_error_rad: { value: step ? Math.abs(step.finalError) : null, reason: stepReason },
    iae_rad_s: { value: integral.iae, reason: null },
    ise_rad2_s: { value: integral.ise, reason: null },
    itae_rad_s2: { value: integral.itae, reason: null },
    control_absolute_nms: { value: integral.controlAbsolute, reason: null },
    control_squared_nm2s: { value: integral.controlSquared, reason: null },
    saturation_fraction: { value: saturation.fraction, reason: null },
    saturation_duration_s: { value: saturation.duration, reason: null },
    maximum_raw_torque_nm: { value: saturation.maximumRawMagnitude, reason: null },
  };
  return (Object.keys(values) as MetricId[]).map((id) => ({
    id,
    label: metricLabel(id),
    value: values[id].value,
    unit: metricUnit(id),
    reason: values[id].reason,
  }));
}

export function metricValue(metrics: readonly MetricValue[], id: MetricId): number | null {
  return metrics.find((metric) => metric.id === id)?.value ?? null;
}

export function evaluateConstraint(definition: ConstraintDefinition, metrics: readonly MetricValue[]): ConstraintResult {
  const value = metricValue(metrics, definition.metric);
  if (value === null) {
    return {
      ...definition,
      value: null,
      state: definition.required ? 'not_met' : 'not_assessed',
      reason: definition.required ? 'Required metric is undefined.' : 'Optional metric is undefined and was not assessed.',
    };
  }
  let passed = false;
  switch (definition.comparator) {
    case '<=': passed = value <= definition.threshold; break;
    case '<': passed = value < definition.threshold; break;
    case '>=': passed = value >= definition.threshold; break;
    case '>': passed = value > definition.threshold; break;
    case 'abs<=': passed = Math.abs(value) <= definition.threshold; break;
  }
  return {
    ...definition,
    value,
    state: passed ? 'met' : 'not_met',
    reason: passed
      ? `Raw value ${value} ${definition.comparator} ${definition.threshold}.`
      : `Raw value ${value} does not satisfy ${definition.comparator} ${definition.threshold}.`,
  };
}

export function analyseScenario(document: ScenarioDocument): RunAnalysis {
  const parsed = safeParseScenarioDocument(document);
  if (!parsed.success) {
    throw new RangeError(parsed.issues.map((issue) => `${issue.path}: ${issue.message}`).join('\n'));
  }
  const started = performance.now();
  const simulation = simulateScenario(document.scenario);
  const stability = evaluateLinearStability(document.scenario.plant, document.scenario.controller);
  let stepMetrics: StepMetrics | null = null;
  let stepMetricsReason: string | null = null;
  if (document.scenario.reference.kind === 'step') {
    stepMetrics = calculateStepMetrics(simulation.samples, document.scenario.reference, document.stepMetricSettings);
  } else {
    stepMetricsReason = 'Step-response metrics require a single step reference profile.';
  }
  const integralMetrics = calculateIntegralMetrics(simulation.samples);
  const saturationMetrics = calculateSaturationMetrics(simulation.samples, document.scenario.actuator.maximumTorque);
  const metrics = buildMetricValues(stepMetrics, stepMetricsReason, integralMetrics, saturationMetrics);
  const constraints = document.constraints.map((constraint) => evaluateConstraint(constraint, metrics));
  const selectedConstraints = constraints.filter((item) => item.required || item.state !== 'not_assessed');
  const constraintsState: EvidenceState = selectedConstraints.length === 0
    ? 'not_assessed'
    : selectedConstraints.every((item) => item.state === 'met') ? 'met' : 'not_met';
  const warnings: string[] = [];
  if (document.scenario.mode === 'aircraft_pitch_surrogate') {
    warnings.push('Aircraft mode is an equivalent pitch-axis surrogate, not a full longitudinal flight-dynamics model.');
  }
  if (!document.parameterProvenance.trim()) warnings.push('Plant parameter provenance is not recorded.');
  if (document.scenario.plant.damping < 0 || document.scenario.plant.stiffness < 0) {
    warnings.push('Negative damping or stiffness represents a locally destabilising surrogate term and requires explicit provenance.');
  }
  if (stability.state !== 'stable_for_scoped_linear_model') {
    warnings.push('The strict Hurwitz pre-check does not show stability for the scoped linear unsaturated model.');
  }
  if (saturationMetrics.duration > 0) {
    warnings.push(`Actuator saturation occurred for ${saturationMetrics.duration.toPrecision(5)} s under the sampled-indicator trapezoidal definition.`);
  }
  if (stepMetrics?.settlingTime === null) warnings.push(stepMetrics.settlingReason ?? 'The response did not settle in the simulated window.');
  if (document.scenario.noise.enabled) warnings.push('One seeded noise realisation is reproducible but is not a statistical validation study.');
  for (const result of constraints.filter((item) => item.state === 'not_met')) {
    const rationale = (result.rationale || result.metric).replace(/[.]+$/, '');
    warnings.push(`Constraint not met: ${rationale}.`);
  }
  const final = simulation.samples.at(-1)!;
  const traces: CalculationTraceItem[] = [
    {
      id: 'AC-TRACE-PLANT',
      title: 'Plant angular acceleration',
      formula: 'ωdot = (u_sat + d - cω - kθ) / I',
      substitution: `(${final.appliedTorque} + ${final.disturbanceTorque} - ${document.scenario.plant.damping}×${final.angularRate} - ${document.scenario.plant.stiffness}×${final.angle}) / ${document.scenario.plant.inertia}`,
      result: `${(final.appliedTorque + final.disturbanceTorque - document.scenario.plant.damping * final.angularRate - document.scenario.plant.stiffness * final.angle) / document.scenario.plant.inertia} rad/s² at the final sample`,
      state: 'met',
      note: 'Signed damping and stiffness are preserved exactly as entered.',
    },
    {
      id: 'AC-TRACE-PID',
      title: 'PID command at final sample',
      formula: 'u_raw = Kp e + xI - Kd ω_used',
      substitution: `${document.scenario.controller.kp}×${final.error} + ${final.integralTorque} - ${document.scenario.controller.kd}×${final.rateUsedByDerivative}`,
      result: `${final.rawTorque} N·m raw, ${final.appliedTorque} N·m applied`,
      state: final.saturationExceeded ? 'not_met' : 'met',
      note: 'Derivative action uses measured rate, so a reference step does not directly create derivative kick.',
    },
    {
      id: 'AC-TRACE-SAT',
      title: 'Actuator saturation',
      formula: 'u_sat = clamp(u_raw, -u_max, +u_max)',
      substitution: `clamp(${final.rawTorque}, -${document.scenario.actuator.maximumTorque}, +${document.scenario.actuator.maximumTorque})`,
      result: `${final.appliedTorque} N·m`,
      state: saturationMetrics.duration === 0 ? 'met' : 'not_met',
      note: 'Saturation is diagnostic, not automatically a failed design unless a recorded constraint requires it.',
    },
    {
      id: 'AC-TRACE-IAE',
      title: 'Integral absolute error',
      formula: 'IAE = trapz(|e|)',
      substitution: `${simulation.samples.length} event-aligned samples from ${simulation.samples[0]!.time} to ${final.time} s`,
      result: `${integralMetrics.iae} rad·s`,
      state: 'met',
      note: 'Trapezoidal integration uses raw unrounded error values.',
    },
    {
      id: 'AC-TRACE-STABILITY',
      title: 'Linear unsaturated Hurwitz pre-check',
      formula: stability.branch.replaceAll('_', ' '),
      substitution: `Coefficients [${stability.coefficientsDescending.join(', ')}]`,
      result: stability.state === 'stable_for_scoped_linear_model' ? 'All applicable strict inequalities pass.' : 'At least one applicable strict inequality fails.',
      state: stability.state === 'stable_for_scoped_linear_model' ? 'met' : 'not_met',
      note: 'This analytical result does not include saturation, noise, nonlinearities or omitted vehicle dynamics.',
    },
  ];
  return {
    simulation,
    stability,
    stepMetrics,
    stepMetricsReason,
    integralMetrics,
    saturationMetrics,
    metrics,
    constraints,
    constraintsState,
    warnings,
    traces,
    runtimeMs: performance.now() - started,
  };
}

export function runTimeStepRefinement(document: ScenarioDocument): { coarse: RunAnalysis; fine: RunAnalysis; result: RefinementResult } {
  const coarse = analyseScenario(document);
  const fineDocument: ScenarioDocument = {
    ...document,
    scenario: {
      ...document.scenario,
      simulation: {
        ...document.scenario.simulation,
        nominalStep: document.scenario.simulation.nominalStep / 2,
      },
    },
  };
  const fine = analyseScenario(fineDocument);
  const result = compareTimeSteps(coarse.simulation, fine.simulation, document.refinementCriteria, document.scenario.noise.enabled);
  return { coarse, fine, result };
}

function canonicalise(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalise);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .filter(([key]) => !['createdAt', 'updatedAt'].includes(key))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => [key, canonicalise(item)]));
  }
  return value;
}

export function canonicalScenarioJson(document: ScenarioDocument): string {
  return JSON.stringify(canonicalise({
    schemaVersion: document.schemaVersion,
    name: document.name,
    intendedQuestion: document.intendedQuestion,
    parameterProvenance: document.parameterProvenance,
    assumptions: document.assumptions,
    displayAngleUnit: document.displayAngleUnit,
    scenario: document.scenario,
    stepMetricSettings: document.stepMetricSettings,
    refinementCriteria: document.refinementCriteria,
    constraints: document.constraints,
    tuning: document.tuning,
    engineVersion: ENGINE_VERSION,
  }));
}

export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function createSavedRun(document: ScenarioDocument, refinement: RefinementResult | null = null): Promise<SavedRun> {
  const analysis = analyseScenario(document);
  const hash = await sha256Hex(canonicalScenarioJson(document));
  return {
    id: createId('run'),
    scenarioId: document.id,
    scenarioName: document.name,
    createdAt: new Date().toISOString(),
    scenarioHash: hash,
    engineVersion: ENGINE_VERSION,
    appVersion: APP_VERSION,
    status: 'completed',
    scenarioSnapshot: structuredClone(document),
    analysis,
    refinement,
  };
}

export function safeParseSavedRun(input: unknown): SafeParseResult<SavedRun> {
  const structural = savedRunSchema.safeParse(input);
  if (!structural.success) return { success: false, issues: zodIssues(structural.error) };

  const issues: ValidationIssue[] = [];
  const candidate = structural.data as unknown as SavedRun;
  for (const key of ['id', 'scenarioId', 'scenarioName', 'createdAt', 'scenarioHash', 'engineVersion', 'appVersion'] as const) {
    if (typeof candidate[key] !== 'string' || !candidate[key]!.trim()) pushIssue(issues, key, `${key} must be non-empty text.`);
  }
  if (candidate.status !== 'completed') pushIssue(issues, 'status', 'Only completed immutable run records are supported.');
  if (typeof candidate.scenarioHash === 'string' && !/^[0-9a-f]{64}$/i.test(candidate.scenarioHash)) {
    pushIssue(issues, 'scenarioHash', 'Scenario hash must be a 64-character SHA-256 hexadecimal value.');
  }
  const snapshot = safeParseScenarioDocument(candidate.scenarioSnapshot);
  if (!snapshot.success) pushIssue(issues, 'scenarioSnapshot', 'Scenario snapshot failed runtime validation.');
  if (snapshot.success && candidate.scenarioId !== snapshot.data.id) pushIssue(issues, 'scenarioId', 'Run scenario ID must match its immutable snapshot.');
  const analysis = candidate.analysis as RunAnalysis | undefined;
  const samples = analysis?.simulation?.samples;
  if (!Array.isArray(samples) || samples.length === 0) {
    pushIssue(issues, 'analysis.simulation.samples', 'A completed run requires a non-empty sampled history.');
  } else if (samples.length > RESOURCE_LIMITS.simulationOutputPoints) {
    pushIssue(issues, 'analysis.simulation.samples', `Saved history exceeds the disclosed ${RESOURCE_LIMITS.simulationOutputPoints}-point limit.`);
  } else {
    const requiredNumeric = ['time', 'referenceAngle', 'angle', 'angularRate', 'error', 'rawTorque', 'appliedTorque'] as const;
    for (let index = 0; index < samples.length; index += 1) {
      const sample = samples[index] as unknown as Record<string, unknown>;
      if (typeof sample !== 'object' || sample === null || requiredNumeric.some((key) => !finite(sample[key]))) {
        pushIssue(issues, `analysis.simulation.samples[${index}]`, 'Sample contains missing or non-finite required values.');
        break;
      }
    }
  }
  return issues.length === 0
    ? { success: true, data: candidate as SavedRun }
    : { success: false, issues };
}

export function evaluateTuningCandidate(
  document: ScenarioDocument,
  candidateController: ControlScenario['controller'],
  candidateId: string,
): TuningEvaluation {
  const candidateDocument: ScenarioDocument = {
    ...document,
    scenario: { ...document.scenario, controller: candidateController },
  };
  const stability = evaluateLinearStability(candidateDocument.scenario.plant, candidateController);
  if (document.tuning.skipAnalyticallyUnstable && stability.state !== 'stable_for_scoped_linear_model') {
    const candidate: TuningCandidate = {
      id: candidateId,
      feasible: false,
      objective: null,
      overshootPercent: null,
      saturationFraction: null,
      controlSquared: null,
      kp: candidateController.kp,
      ki: candidateController.ki,
      kd: candidateController.kd,
      derivativeFilterTimeConstant: candidateController.derivativeFilterTimeConstant,
      trackingTimeConstant: candidateController.antiWindup.enabled ? candidateController.antiWindup.trackingTimeConstant : 0,
    };
    return { candidate, stability, constraintResults: [], infeasibilityReasons: ['Skipped because the strict linear unsaturated Hurwitz pre-check failed.'] };
  }
  try {
    const analysis = analyseScenario(candidateDocument);
    const constraintResults = document.tuning.constraints.map((constraint) => evaluateConstraint(constraint, analysis.metrics));
    const infeasibilityReasons = constraintResults.filter((result) => result.required && result.state !== 'met').map((result) => `${result.metric}: ${result.reason}`);
    if (document.tuning.requireLinearStability && stability.state !== 'stable_for_scoped_linear_model') {
      infeasibilityReasons.push('Required linear unsaturated Hurwitz pre-check did not pass.');
    }
    const objective = metricValue(analysis.metrics, document.tuning.primaryObjective);
    if (objective === null) infeasibilityReasons.push(`Primary objective ${document.tuning.primaryObjective} is undefined.`);
    const candidate: TuningCandidate = {
      id: candidateId,
      feasible: infeasibilityReasons.length === 0,
      objective,
      overshootPercent: metricValue(analysis.metrics, 'overshoot_percent'),
      saturationFraction: metricValue(analysis.metrics, 'saturation_fraction'),
      controlSquared: metricValue(analysis.metrics, 'control_squared_nm2s'),
      kp: candidateController.kp,
      ki: candidateController.ki,
      kd: candidateController.kd,
      derivativeFilterTimeConstant: candidateController.derivativeFilterTimeConstant,
      trackingTimeConstant: candidateController.antiWindup.enabled ? candidateController.antiWindup.trackingTimeConstant : 0,
    };
    return { candidate, stability, constraintResults, infeasibilityReasons };
  } catch (error) {
    const candidate: TuningCandidate = {
      id: candidateId,
      feasible: false,
      objective: null,
      overshootPercent: null,
      saturationFraction: null,
      controlSquared: null,
      kp: candidateController.kp,
      ki: candidateController.ki,
      kd: candidateController.kd,
      derivativeFilterTimeConstant: candidateController.derivativeFilterTimeConstant,
      trackingTimeConstant: candidateController.antiWindup.enabled ? candidateController.antiWindup.trackingTimeConstant : 0,
    };
    return { candidate, stability, constraintResults: [], infeasibilityReasons: [error instanceof Error ? error.message : String(error)] };
  }
}

export function runTuning(
  document: ScenarioDocument,
  isCancelled: () => boolean = () => false,
  onProgress: (completed: number, total: number) => void = () => undefined,
): TuningResult {
  const started = performance.now();
  const count = tuningCandidateCount(document.tuning, document.scenario.controller.antiWindup.enabled);
  const effectiveCap = Math.min(document.tuning.maximumCandidates, RESOURCE_LIMITS.tuningCandidates);
  if (count > effectiveCap) throw new RangeError(`Candidate count ${count} exceeds the disclosed software cap ${effectiveCap}.`);
  const evaluations: TuningEvaluation[] = [];
  let ordinal = 0;
  const ttValues = document.scenario.controller.antiWindup.enabled ? document.tuning.grid.trackingTimeConstant : [0];
  for (const kp of document.tuning.grid.kp) {
    for (const ki of document.tuning.grid.ki) {
      for (const kd of document.tuning.grid.kd) {
        for (const derivativeFilterTimeConstant of document.tuning.grid.derivativeFilterTimeConstant) {
          for (const trackingTimeConstant of ttValues) {
            if (isCancelled()) {
              return {
                state: 'cancelled', candidateCount: count, feasibleCount: evaluations.filter((item) => item.candidate.feasible).length,
                selectedCandidate: null, evaluations, primaryObjective: document.tuning.primaryObjective,
                elapsedMs: performance.now() - started, selectionTrace: ['The tuning operation was cancelled. No recommendation was produced.'],
              };
            }
            const controller: ControlScenario['controller'] = {
              kp, ki, kd, derivativeFilterTimeConstant,
              antiWindup: document.scenario.controller.antiWindup.enabled
                ? { enabled: true, trackingTimeConstant }
                : { enabled: false },
            };
            evaluations.push(evaluateTuningCandidate(document, controller, makeCandidateId(ordinal, count)));
            ordinal += 1;
            onProgress(ordinal, count);
          }
        }
      }
    }
  }
  const selectedCandidate = rankFeasibleCandidates(evaluations.map((item) => item.candidate));
  const feasibleCount = evaluations.filter((item) => item.candidate.feasible).length;
  const selectionTrace = selectedCandidate
    ? [
        `${feasibleCount} of ${count} candidates met every required constraint.`,
        `Primary objective ${document.tuning.primaryObjective} was sorted ascending.`,
        'Exact tie-break order: objective, overshoot, saturation fraction, squared control effort, Kp, Ki, Kd, filter time constant, tracking time constant, canonical candidate ID.',
        `Selected ${selectedCandidate.id}; no weighted or least-violation score was used.`,
      ]
    : [
        `0 of ${count} candidates had a defined objective and met every required constraint.`,
        'No controller recommendation was produced. AeroControl does not replace this result with a penalty score.',
      ];
  return {
    state: 'completed', candidateCount: count, feasibleCount, selectedCandidate, evaluations,
    primaryObjective: document.tuning.primaryObjective, elapsedMs: performance.now() - started, selectionTrace,
  };
}
